import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Package, ShoppingCart, MapPin, User, BookOpen } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

const BottomNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getCartCount, setIsCartOpen, getCartTotal, formatPrice } = useCart();
  const { theme } = useTheme();
  const { user, loading: authLoading, getEffectiveCustomerType } = useAuth();
  const cartCount = getCartCount();
  const cartTotal = getCartTotal();

  // El blog se muestra a usuarios comercio + staff. Consumidores finales no lo ven.
  const isLocalAdmin = typeof window !== 'undefined' && localStorage.getItem('bebida-bacana-admin') === 'true';
  const isStaff = isLocalAdmin || user?.role === 'admin' || user?.role === 'seller';
  const effectiveType = getEffectiveCustomerType ? getEffectiveCustomerType() : user?.customer_type;
  // Hint: si hay un session_token en localStorage, casi con certeza va a
  // haber un `user` una vez que termine el checkAuth. Lo usamos para
  // NO ocultar "Blog" ni "Cuenta" durante el flicker de carga.
  const hasSessionHint = typeof window !== 'undefined' && !!localStorage.getItem('session_token');
  const showBlog = isStaff || effectiveType === 'comercio' || (authLoading && hasSessionHint);
  // "Mi cuenta" visible para cualquier user logueado (Consumidor Final o
  // Comercio) o para staff. También la mostramos si estamos en pleno
  // authLoading con un session_token pendiente, para evitar que el ícono
  // aparezca y desaparezca entre navegaciones ("a veces está, a veces no").
  const showAccount = !!user || isStaff || (authLoading && hasSessionHint);

  const navItems = [
    { icon: Package, label: 'Catálogo', path: '/products', testId: 'nav-catalog' },
    ...(showBlog ? [{ icon: BookOpen, label: 'Blog', path: '/blog', testId: 'nav-blog' }] : []),
    { icon: MapPin, label: 'Contacto', path: '/contact', testId: 'nav-contact' },
    ...(showAccount ? [{ icon: User, label: 'Cuenta', path: '/account', testId: 'nav-account' }] : []),
    { icon: ShoppingCart, label: 'Carrito', action: 'cart', testId: 'nav-cart' },
  ];
  const gridCols = navItems.length === 5 ? 'grid-cols-5' : navItems.length === 4 ? 'grid-cols-4' : 'grid-cols-3';

  const isActive = (item) => {
    if (item.action === 'cart') return false;
    return location.pathname === item.path || (item.path === '/products' && location.pathname === '/');
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-slate-100 z-50" style={{ minHeight: '60px' }}>
      <div className={`grid ${gridCols} h-[60px] max-w-lg mx-auto`}>
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item);
          return (
            <button
              key={item.label}
              onClick={() => item.action === 'cart' ? setIsCartOpen(true) : navigate(item.path)}
              data-testid={item.testId}
              className='flex flex-col items-center justify-center gap-0.5 transition-colors'
              style={{ color: active ? theme.primary : '#94A3B8' }}
            >
              <div className="relative">
                <Icon size={19} strokeWidth={active ? 2.5 : 2} />
                {item.action === 'cart' && cartCount > 0 && (
                  <span className="absolute -top-1.5 -right-2.5 text-white text-[10px] font-bold rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center" style={{ backgroundColor: theme.primary }}>
                    {cartCount}
                  </span>
                )}
              </div>
              {item.action === 'cart' && cartCount > 0 ? (
                <span className="text-[10px] font-bold" style={{ color: theme.primary }}>
                  ${Math.round(cartTotal / 1000)}k
                </span>
              ) : (
                <span className={`text-[10px] ${active ? 'font-semibold' : 'font-medium'}`}>{item.label}</span>
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNavigation;
